import * as React from "react";
import {
  makeStyles,
  Theme,
  createStyles,
  DialogActions,
  Button,
  Checkbox,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
} from "@material-ui/core";
import { useHubx } from "../../../HubX";
import { useSnackbar } from "notistack";
import EDirector from "../../../core/contracts/EDirector";
import { ALL_DIRECTORS, POST_USER_DIRECTOR } from "../../../Fedx";
import { Skeleton } from "@material-ui/lab";
import { isArrayValid } from "../../../helpers/utility";
import { isEqual } from "lodash";
import useConstructor from "../../../core/hooks/useConstructor";
import EUserDirectorRequest from "../../../core/contracts/EUserDirectorRequest";
import EUserDirectorEmail from "../../../core/contracts/EUserDirectorEmail";
import { EVENT_UPDATE_USER_DIRECTOR } from "../../../Events";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(3, 1),
    },

    addButton: {
      color: theme.palette.common.white,
    },

    tableWrapper: {
      "margin": theme.spacing(2, 0),

      "& .MuiTableCell-stickyHeader": {
        backgroundColor: "#1D212A",
        color: theme.palette.common.white,
      },
    },
  })
);

type DirectorManagerDialogProps = {
  handleClickCancel: () => void;
  userDirectors: EUserDirectorEmail;
};

function DirectorManagerDialog({ handleClickCancel, userDirectors }: DirectorManagerDialogProps) {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [directors, setDirectors] = React.useState<EDirector[]>([]);
  const [selectedDirectors, setSelectedDirectors] = React.useState<string[]>([]);

  const hub = useHubx();
  const toastr = useSnackbar();
  const classes = useStyles();

  const allDirectorCodes = React.useMemo(() => {
    if (!directors) {
      return [];
    }

    return directors.map((x) => x.id || "");
  }, [directors]);

  const getDirectors = async () => {
    setLoading(true);
    try {
      const APIResponse: EDirector[] = await hub.request(ALL_DIRECTORS);
      setDirectors(APIResponse);
      setSelectedDirectors(
        userDirectors.diretorias.flatMap((ud) => {
          return ud.diretoria?.id || "";
        })
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      toastr.enqueueSnackbar("Ops! Não foi possível recuperar as diretorias!", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (userCode: string) => {
    if (selectedDirectors.includes(userCode)) {
      setSelectedDirectors((prev) => prev.filter((x) => x !== userCode));
    } else {
      setSelectedDirectors((prev) => [...prev, userCode]);
    }
  };

  const handleClickSelectAll = () => {
    if (isEqual(allDirectorCodes, selectedDirectors)) {
      setSelectedDirectors([]);
    } else {
      setSelectedDirectors(allDirectorCodes);
    }
  };

  useConstructor(() => {
    getDirectors();
  });

  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directors]);

  const doCancel = () => {
    setSelectedDirectors([]);
    setDirectors([]);
    handleClickCancel();
  };

  const handleCLickAdd = async () => {
    const newDirector: EUserDirectorRequest[] = selectedDirectors.map((d) => {
      return {
        user_email: userDirectors.email,
        director_id: d,
      };
    });

    if (newDirector.length === 0) {
      newDirector.push({
        user_email: userDirectors.email,
        director_id: "00000000-0000-0000-0000-000000000000",
      });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response: any = await hub.request(POST_USER_DIRECTOR, newDirector);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const responseUpdate = response.map((ru: any) => {
      return {
        id: ru.id,
        diretoria: {
          id: ru.director_id,
          name: directors.find((d) => d.id === ru.director_id)?.name,
        },
        usuario: {
          nome: "",
        },
      };
    });
    hub.notify(EVENT_UPDATE_USER_DIRECTOR, {
      email: userDirectors.email,
      directors: responseUpdate,
    });

    doCancel();
  };

  const isOnError = false;
  const loadingState = new Array(3).fill({});
  const renderValue = (value?: string | number | null) => {
    return value || value === 0 ? value : "-";
  };

  return (
    <div className={classes.root}>
      <div>Associar as seguintes diretorias ao usuário</div>
      <div className={classes.tableWrapper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={
                    isArrayValid(allDirectorCodes) &&
                    isEqual(allDirectorCodes.sort(), selectedDirectors.sort())
                  }
                  onClick={handleClickSelectAll}
                />
              </TableCell>
              <TableCell>Selecionar todas</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {!loading &&
              directors?.map((director) => (
                <TableRow key={director.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedDirectors.includes(director?.id || "")}
                      onClick={() => handleClick(director?.id || "")}
                    />
                  </TableCell>
                  <TableCell>{renderValue(director?.name)}</TableCell>
                </TableRow>
              ))}

            {loading &&
              loadingState.map((_, idx) => (
                <TableRow key={`loading_row_${idx}`}>
                  <TableCell colSpan={3}>
                    <Skeleton />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      <DialogActions>
        <Button variant="outlined" onClick={doCancel}>
          Cancelar
        </Button>

        <Tooltip title={isOnError ? "Por favor, selecione diretorias" : ""}>
          <span>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCLickAdd}
              className={classes.addButton}
              disabled={isOnError}
            >
              Salvar
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </div>
  );
}

export default DirectorManagerDialog;
